import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { NewStrategyButton } from '@/components/layout/top-bar-global-actions';
import { TopBarActions } from '@/components/layout/top-bar-slots';
import { StrategiesListPage } from '@/components/strategies-list/strategies-list-page';
import {
  resolveSortKey,
  type ColumnKey,
} from '@/components/strategies-list/columns/strategies-list-columns';
import { useDraftActions } from '@/components/strategies-list/drafts/use-draft-actions';
import { STRATEGY_LIST_FILTERS } from '@/components/strategies-list/filters/strategies-list-filter';
import type { SortState } from '@/components/strategies-list/table/strategies-list-table';
import { useColumnVisibility } from '@/components/strategies-list/columns/use-column-visibility';
import { useBusinessModel } from '@/hooks/data/use-auth';
import {
  STRATEGIES_PAGE_SIZE,
  useStrategiesIndexData,
} from '@/components/strategies-list/use-strategies-index-data';
import { loadNamespaces } from '@/lib/i18n/i18n';

const SORT_KEYS = [
  'name',
  'status',
  'daily_budget',
  'spend',
  'impressions',
  'clicks',
  'roas',
  'conversions',
  'conversion_value',
  'trend',
] as const;

const searchSchema = z.object({
  date_range: z
    .enum(['LAST_7D', 'LAST_14D', 'LAST_30D', 'LAST_90D', 'CUSTOM'])
    .default('LAST_30D'),
  start_date: z.string().optional(),
  end_date: z.string().optional(),
  filter: z.enum(STRATEGY_LIST_FILTERS).default('all'),
  q: z.string().optional(),
  page: z.coerce.number().int().positive().default(1),
  sort: z.enum(SORT_KEYS).default('spend'),
  dir: z.enum(['asc', 'desc']).default('desc'),
});

export const Route = createFileRoute('/_authenticated/strategies/')({
  loader: () => loadNamespaces('strategy'),
  validateSearch: searchSchema,
  staticData: {
    title: 'route.strategies.title',
    messageContext: { context_type: 'workspace' },
  },
  component: StrategiesIndexRoute,
});

function StrategiesIndexRoute(): React.ReactElement {
  const businessModel = useBusinessModel();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { visible, toggle } = useColumnVisibility();
  const { range, current, items, kpis, kpisFailed } =
    useStrategiesIndexData(search);
  const { resumeDraft, discardDraft, openStrategy, openRow } =
    useDraftActions();

  const sort: SortState = {
    key: resolveSortKey(search.sort, businessModel),
    dir: search.dir,
  };

  const updateSearch = (
    next: Partial<typeof search>,
    options: { resetPage?: boolean } = {},
  ): void => {
    void navigate({
      to: '/strategies',
      search: {
        ...search,
        ...next,
        ...(options.resetPage ? { page: 1 } : {}),
      },
      replace: true,
    });
  };

  return (
    <>
      <TopBarActions>
        <NewStrategyButton location="strategies_page" />
      </TopBarActions>
      <StrategiesListPage
        businessModel={businessModel}
        items={items}
        total={current.data?.total ?? 0}
        // Pending, not isLoading: a paused fetch holds no data either.
        isLoading={current.isPending}
        isError={current.isError}
        kpis={kpis}
        kpisFailed={kpisFailed}
        range={range}
        onRangeChange={(next) =>
          updateSearch(
            {
              date_range: next.date_range,
              start_date: next.start_date,
              end_date: next.end_date,
            },
            { resetPage: true },
          )
        }
        search={search.q ?? ''}
        onSearchChange={(next) =>
          updateSearch({ q: next || undefined }, { resetPage: true })
        }
        filter={search.filter}
        onFilterChange={(next) =>
          updateSearch({ filter: next }, { resetPage: true })
        }
        sort={sort}
        onSortChange={(next) => updateSearch({ sort: next.key, dir: next.dir })}
        page={search.page}
        pageSize={STRATEGIES_PAGE_SIZE}
        onPageChange={(next) => updateSearch({ page: next })}
        visibleColumns={visible}
        onToggleColumn={(key: ColumnKey) => toggle(key)}
        onRowClick={openRow}
        onResumeDraft={resumeDraft}
        onDiscardDraft={discardDraft}
        onOpenDraft={openStrategy}
      />
    </>
  );
}
